import React, { useState } from 'react';
import { useAuth } from '../lib/auth';

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    const result = await login(password);
    setSubmitting(false);
    if (result === 'ok') return;
    if (result === 'credentials') {
      setError('Incorrect password.');
      setPassword('');
      return;
    }
    setError('Unable to sign in. Please try again.');
  };

  return (
    <div style={s.root}>
      <div style={s.left}>
        <div style={s.brand}>
          <span style={s.brandIcon}>🚗</span>
          <span style={s.brandName}>Friends in Cars</span>
        </div>
        <div style={s.hero}>
          <div style={s.heroTag}>ADVERTISER PLATFORM</div>
          <h1 style={s.heroTitle}>Reach riders at the moment that matters.</h1>
          <p style={s.heroSub}>
            Target real commuters with contextual offers delivered during live rides. 
            Full campaign control. Real-time analytics.
          </p>
          <div style={s.stats}>
            <div style={s.stat}>
              <div style={s.statNum}>94%</div>
              <div style={s.statLabel}>Offer open rate</div>
            </div>
            <div style={s.statDivider} />
            <div style={s.stat}>
              <div style={s.statNum}>2.4x</div>
              <div style={s.statLabel}>Avg. redemption vs display</div>
            </div>
            <div style={s.statDivider} />
            <div style={s.stat}>
              <div style={s.statNum}>$0.05</div>
              <div style={s.statLabel}>Starting bid per impression</div>
            </div>
          </div>
        </div>
        <div style={s.leftFooter}>© 2026 Friends in Cars, LLC · Confidential</div>
      </div>

      <div style={s.right}>
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h2 style={s.cardTitle}>Advertiser Sign In</h2>
            <p style={s.cardSub}>Enter your access credentials to manage campaigns.</p>
          </div>
          <form onSubmit={handleSubmit} style={s.form}>
            <div style={s.fieldWrap}>
              <label style={s.label}>Access Password</label>
              <input
                type="password"
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                placeholder="••••••••"
                style={s.input}
                autoFocus
              />
              {error && <div style={s.error}>{error}</div>}
            </div>
            <button type="submit" style={s.btn} disabled={submitting}>
              {submitting ? 'Signing in…' : 'Access Dashboard →'}
            </button>
          </form>
          <div style={s.cardFooter}>
            Need access? Contact <span style={s.link}>partnerships@friendsincars.com</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight: '100vh',
    display: 'flex',
    fontFamily: "'Inter', sans-serif",
  },
  left: {
    flex: 1,
    background: '#111827',
    padding: '48px 56px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: '100vh',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  brandIcon: {
    fontSize: 24,
  },
  brandName: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: '-0.02em',
  },
  hero: {
    maxWidth: 480,
  },
  heroTag: {
    color: '#4ade80',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.12em',
    marginBottom: 20,
  },
  heroTitle: {
    color: '#ffffff',
    fontSize: 40,
    fontWeight: 800,
    lineHeight: 1.15,
    letterSpacing: '-0.03em',
    margin: '0 0 20px 0',
  },
  heroSub: {
    color: '#9ca3af',
    fontSize: 16,
    lineHeight: 1.7,
    margin: '0 0 48px 0',
  },
  stats: {
    display: 'flex',
    alignItems: 'center',
    gap: 32,
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  statNum: {
    color: '#ffffff',
    fontSize: 28,
    fontWeight: 800,
    letterSpacing: '-0.02em',
  },
  statLabel: {
    color: '#6b7280',
    fontSize: 12,
    lineHeight: 1.4,
  },
  statDivider: {
    width: 1,
    height: 40,
    background: '#1f2937',
  },
  leftFooter: {
    color: '#374151',
    fontSize: 12,
  },
  right: {
    width: 480,
    background: '#f4f5f7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 48,
  },
  card: {
    background: '#ffffff',
    borderRadius: 16,
    padding: '40px 40px',
    width: '100%',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 32px rgba(0,0,0,0.06)',
  },
  cardHeader: {
    marginBottom: 32,
  },
  cardTitle: {
    color: '#111827',
    fontSize: 22,
    fontWeight: 700,
    margin: '0 0 8px 0',
    letterSpacing: '-0.02em',
  },
  cardSub: {
    color: '#6b7280',
    fontSize: 14,
    margin: 0,
    lineHeight: 1.5,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  fieldWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  label: {
    color: '#374151',
    fontSize: 13,
    fontWeight: 500,
  },
  input: {
    background: '#f9fafb',
    border: '1.5px solid #e5e7eb',
    borderRadius: 8,
    color: '#111827',
    padding: '12px 14px',
    fontSize: 15,
    fontFamily: 'inherit',
    width: '100%',
  },
  error: {
    color: '#dc2626',
    fontSize: 13,
    marginTop: 2,
  },
  btn: {
    background: '#111827',
    color: '#ffffff',
    border: 'none',
    borderRadius: 8,
    padding: '14px 20px',
    fontSize: 15,
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
    letterSpacing: '-0.01em',
  },
  cardFooter: {
    marginTop: 24,
    color: '#9ca3af',
    fontSize: 12,
    textAlign: 'center',
  },
  link: {
    color: '#111827',
    fontWeight: 500,
    cursor: 'pointer',
  },
};

export default LoginPage;